import React from 'react';
import { Shield, AlertTriangle, Lock } from 'lucide-react';
import useAdminStore from '../store';

const Guard = ({ 
  children, 
  permission, 
  permissions = [], 
  role, 
  roles = [],
  fallback,
  showMessage = true,
  className = ''
}) => {
  const { currentUser } = useAdminStore();

  // Check permissions
  const hasPermission = () => {
    if (!currentUser) return false;

    // Admin has access to everything
    if (currentUser.role === 'admin' || currentUser.effectivePermissions.includes('*')) {
      return true;
    }

    // Check specific permission
    if (permission) {
      return currentUser.effectivePermissions.includes(permission);
    }

    // Check any of the permissions
    if (permissions.length > 0) {
      return permissions.some(perm => currentUser.effectivePermissions.includes(perm));
    }

    // Check specific role
    if (role) {
      return currentUser.role === role;
    }

    // Check any of the roles
    if (roles.length > 0) {
      return roles.includes(currentUser.role);
    }

    // If no specific checks, allow access
    return true;
  };

  // If user has permission, render children
  if (hasPermission()) {
    return <>{children}</>;
  }

  // If fallback is provided, render it
  if (fallback) {
    return fallback;
  }

  // If showMessage is false, render nothing
  if (!showMessage) {
    return null;
  }

  // Default access denied message
  return (
    <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-6 ${className}`}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Lock className="h-6 w-6 text-yellow-600" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">
            Access Restricted
          </h3>
          <div className="mt-1 text-sm text-yellow-700">
            <p>You don't have permission to access this feature.</p>
            {(permission || permissions.length > 0) && (
              <p className="mt-1">
                <span className="font-medium">Required permission:</span>{' '}
                {permission || permissions.join(', ')}
              </p>
            )}
            {(role || roles.length > 0) && (
              <p className="mt-1">
                <span className="font-medium">Required role:</span>{' '}
                {role || roles.join(', ')}
              </p>
            )}
            <p className="mt-1">
              <span className="font-medium">Your role:</span> {currentUser?.role || 'None'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Higher-order component for route protection
export const withGuard = (Component, guardProps = {}) => {
  return (props) => (
    <Guard {...guardProps}>
      <Component {...props} />
    </Guard>
  );
};

// Hook for checking permissions
export const usePermissions = () => {
  const { currentUser } = useAdminStore();

  const hasPermission = (permission) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin' || currentUser.effectivePermissions.includes('*')) {
      return true;
    }
    return currentUser.effectivePermissions.includes(permission);
  };

  const hasAnyPermission = (permissions) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin' || currentUser.effectivePermissions.includes('*')) {
      return true;
    }
    return permissions.some(perm => currentUser.effectivePermissions.includes(perm));
  };

  const hasRole = (role) => {
    if (!currentUser) return false;
    return currentUser.role === role;
  };

  const hasAnyRole = (roles) => {
    if (!currentUser) return false;
    return roles.includes(currentUser.role);
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    currentUser,
    isAdmin: currentUser?.role === 'admin'
  };
};

// Component for inline permission checks
export const PermissionCheck = ({ 
  permission, 
  permissions = [], 
  children, 
  fallback = null 
}) => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  
  const allowed = permission 
    ? hasPermission(permission)
    : permissions.length > 0 
    ? hasAnyPermission(permissions)
    : true;

  return allowed ? children : fallback;
};

// Component for role-based rendering
export const RoleCheck = ({ 
  role, 
  roles = [], 
  children, 
  fallback = null 
}) => {
  const { hasRole, hasAnyRole } = usePermissions();
  
  const allowed = role 
    ? hasRole(role)
    : roles.length > 0 
    ? hasAnyRole(roles)
    : true;

  return allowed ? children : fallback;
};

// Protected button component
export const ProtectedButton = ({
  permission,
  permissions = [],
  role,
  roles = [],
  children,
  disabled = false,
  className = '',
  disabledTitle = 'You don\'t have permission to perform this action',
  ...props
}) => {
  const { hasPermission, hasAnyPermission, hasRole, hasAnyRole } = usePermissions();
  
  const allowed = permission 
    ? hasPermission(permission)
    : permissions.length > 0 
    ? hasAnyPermission(permissions)
    : role
    ? hasRole(role)
    : roles.length > 0
    ? hasAnyRole(roles)
    : true;

  const isDisabled = disabled || !allowed;

  return (
    <button
      {...props}
      disabled={isDisabled}
      title={!allowed ? disabledTitle : props.title}
      className={`${className} ${!allowed ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

// Error boundary for permission-related errors
export class PermissionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Permission error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Permission Error
              </h3>
              <div className="mt-1 text-sm text-red-700">
                <p>There was an error checking your permissions.</p>
                {this.props.showDetails && this.state.error && (
                  <details className="mt-2">
                    <summary className="cursor-pointer font-medium">
                      Error Details
                    </summary>
                    <pre className="mt-1 text-xs bg-red-100 p-2 rounded overflow-auto">
                      {this.state.error.message}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default Guard;
